import { Box, IconButton, Divider, Button } from '@mui/joy';
import {
  TextB as FormatBold,
  TextItalic as FormatItalic,
  ListBullets as FormatListBulleted,
  ListNumbers as FormatListNumbered,
  Table as TableChart,
  Image as InsertPhoto,
  LinkSimple as InsertLink,
  ArrowCounterClockwise as Undo,
  ArrowClockwise as Redo,
} from '@phosphor-icons/react';
import { Editor } from '@tiptap/react';

interface EditorToolbarProps {
  editor: Editor;
}

export function EditorToolbar({ editor }: EditorToolbarProps) {
  const addTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl);
    
    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {/* Undo/Redo */}
      <IconButton
        size="sm"
        variant={editor.can().undo() ? 'plain' : 'soft'}
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo />
      </IconButton>
      <IconButton
        size="sm"
        variant={editor.can().redo() ? 'plain' : 'soft'}
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo />
      </IconButton>

      <Divider orientation="vertical" />

      {/* Text formatting */}
      <IconButton
        size="sm"
        variant={editor.isActive('bold') ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FormatBold />
      </IconButton>
      <IconButton
        size="sm"
        variant={editor.isActive('italic') ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FormatItalic />
      </IconButton>

      <Divider orientation="vertical" />

      {/* Paragraph styles */}
      <Button
        size="sm"
        variant={editor.isActive('paragraph') ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().setParagraph().run()}
      >
        P
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 1 }) ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        H1
      </Button>
      <Button
        size="sm"
        variant={editor.isActive('heading', { level: 2 }) ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </Button>

      <Divider orientation="vertical" />

      {/* Lists */}
      <IconButton
        size="sm"
        variant={editor.isActive('bulletList') ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FormatListBulleted />
      </IconButton>
      <IconButton
        size="sm"
        variant={editor.isActive('orderedList') ? 'solid' : 'plain'}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FormatListNumbered />
      </IconButton>

      <Divider orientation="vertical" />

      {/* Insert elements */}
      <IconButton
        size="sm"
        variant="plain"
        onClick={addTable}
      >
        <TableChart />
      </IconButton>
      <IconButton
        size="sm"
        variant="plain"
        onClick={addImage}
      >
        <InsertPhoto />
      </IconButton>
      <IconButton
        size="sm"
        variant={editor.isActive('link') ? 'solid' : 'plain'}
        onClick={addLink}
      >
        <InsertLink />
      </IconButton>
    </Box>
  );
}